export type SearchType = 'DISEASES' | 'VARIANTS' | 'GENES' | 'CHEMICALS';

export interface FilterConfig {
  value: string;
  label: string;
  displayLabel?: string;
  secondColumnLabel?: string;
  thirdColumnLabel?: string;
}

export interface SearchTypeConfig {
  filters: FilterConfig[];
  columnHeaders: Record<string, string>;
  secondColumnHeaders?: Record<string, string>;
  thirdColumnHeaders?: Record<string, string>;
  resultFields: Record<string, string>;
  secondColumnFields?: Record<string, string>;
  thirdColumnFields?: Record<string, string>;
  formatExamples: Record<string, string>;
}

export const SEARCH_TYPES: SearchType[] = ['DISEASES', 'VARIANTS', 'GENES', 'CHEMICALS'];

export const searchConfig: Record<SearchType, SearchTypeConfig> = {
  DISEASES: {
    filters: [
      { value: 'name', label: 'Disease Name', displayLabel: 'Disease Name' },
      { value: 'diseaseUMLSCUI', label: 'Disease CUI', displayLabel: 'Disease CUI' },
      { value: 'MSH', label: 'MESH', displayLabel: 'MESH' },
      { value: 'OMIM', label: 'OMIM', displayLabel: 'OMIM' },
      { value: 'NCI', label: 'NCI', displayLabel: 'NCI' },
      { value: 'HPO', label: 'HPO', displayLabel: 'HPO' },
      { value: 'MONDO', label: 'MONDO', displayLabel: 'MONDO' },
      { value: 'EFO', label: 'EFO', displayLabel: 'EFO' },
      { value: 'DO', label: 'DO', displayLabel: 'DO' },
    ],
    columnHeaders: {
      name: 'Disease Name',
      diseaseUMLSCUI: 'Disease CUI',
      MSH: 'MESH',
      OMIM: 'OMIM',
      NCI: 'NCI',
      HPO: 'HPO',
      MONDO: 'MONDO',
      EFO: 'EFO',
    },
    secondColumnHeaders: {
      diseaseUMLSCUI: 'UMLS Name',
      name: 'Disease CUI',
      MSH: 'UMLS Name',
      OMIM: 'UMLS Name',
      NCI: 'UMLS Name',
      HPO: 'UMLS Name',
      MONDO: 'UMLS Name',
      EFO: 'UMLS Name',
      DO: 'UMLS Name',
    },
    thirdColumnHeaders: {
      diseaseUMLSCUI: 'Select',
      name: 'UMLS Name',
      MSH: 'Disease CUI',
      OMIM: 'Disease CUI',
      NCI: 'Disease CUI',
      HPO: 'Disease CUI',
      MONDO: 'Disease CUI',
      EFO: 'Disease CUI',
      DO: 'Disease CUI',
    },
    resultFields: {
      name: 'term',
      diseaseUMLSCUI: 'diseaseUMLSCUI',
      MSH: 'msh',
      OMIM: 'omim',
      NCI: 'nci',
      HPO: 'hpo',
      MONDO: 'mondo',
      EFO: 'efo',
    },
    secondColumnFields: {
      name: 'normalizedId',
      diseaseUMLSCUI: 'name',
      MSH: 'name',
      OMIM: 'name',
      NCI: 'name',
      HPO: 'name',
      MONDO: 'name',
      EFO: 'name',
    },
    thirdColumnFields: {
      name: 'normalizedName',
      diseaseUMLSCUI: '',
      MSH: 'diseaseUMLSCUI',
      OMIM: 'diseaseUMLSCUI',
      NCI: 'diseaseUMLSCUI',
      HPO: 'diseaseUMLSCUI',
      MONDO: 'diseaseUMLSCUI',
      EFO: 'diseaseUMLSCUI',
    },
    formatExamples: {
      name: 'Names are: Vascular Diseases, Hypertensive disease, Liver carcinoma',
      diseaseUMLSCUI: 'CUIs are: C0002736, C1862939, C2677565',
      MSH: 'MESH IDs are: D014211, D003922, D003923',
      OMIM: 'OMIM IDs are: 147450, 612069, 602432',
      NCI: 'NCI IDs are: C34373, C34558, C131626',
      HPO: 'HPO IDs are: HP:0009031, HP:0001250, HP:0004322',
      MONDO: 'MONDO IDs are: 0005335, 0008315, 0004950',
      EFO: 'EFO IDs are: 0001357, 0000474, 0000180',
    },
  },
  VARIANTS: {
    filters: [
      { value: 'strID', label: 'VARIANT rs number', displayLabel: 'VARIANT rs number' },
      { value: 'chromocoord', label: 'Chrom. position', displayLabel: 'Chrom. position' },
      { value: 'proteinStrID', label: 'Protein mutation', displayLabel: 'Protein mutation' },
      { value: 'hgvsc', label: 'HGVSc', displayLabel: 'HGVSc' },
      { value: 'hgvsp', label: 'HGVSp', displayLabel: 'HGVSp' },
    ],
    columnHeaders: {
      strID: 'VARIANT rs number',
      chromocoord: 'Chrom. position',
      proteinStrID: 'Protein mutation',
      hgvsc: 'HGVSc',
      hgvsp: 'HGVSp',
    },
    secondColumnHeaders: {
      strID: 'Variant Gene',
      chromocoord: 'Variant Gene',
      proteinStrID: 'Variant Gene',
      hgvsc: 'Variant Gene',
      hgvsp: 'Variant Gene',
    },
    thirdColumnHeaders: {
      strID: 'Select',
      chromocoord: 'VARIANT rs number',
      proteinStrID: 'VARIANT rs number',
      hgvsc: 'VARIANT rs number',
      hgvsp: 'VARIANT rs number',
    },
    resultFields: {
      strID: 'strID',
      chromocoord: 'chromocoord',
      proteinStrID: 'proteinStrID',
      hgvsc: 'hgvsc',
      hgvsp: 'hgvsp',
    },
    secondColumnFields: {
      strID: 'geneSymbol',
      chromocoord: 'geneSymbol',
      proteinStrID: 'geneSymbol',
      hgvsc: 'geneSymbol',
      hgvsp: 'geneSymbol',
    },
    formatExamples: {
      strID: 'rs numbers are: rs75290158, rs142200609, rs893051',
      chromocoord: 'Positions are: 1:11796321, 6:31575254, 7:140753336',
      proteinStrID: 'Protein mutations are: GJB2 Val153Ile, GJB2 V153F, USH2A P2811S',
      hgvsc:
        'HGVSc are: ENST00000402696.9:c.1765C>G, ENST00000272369.14:c.965+2468_965+2497del, ENST00000341426.9:c.843+61_843+62insTGGGGCGACTTCGGTCCCAGGG',
      hgvsp:
        'HGVSp are: ENSP00000385834.3:p.Pro589Ala, ENSP00000369497.3:p.Tyr1739Ter, ENSP00000296930.5:p.Trp288CysfsTer12',
    },
  },
  GENES: {
    filters: [
      { value: 'name', label: 'Gene Name', displayLabel: 'Gene Name' },
      { value: 'symbol', label: 'Gene symbol', displayLabel: 'Gene Symbol' },
      { value: 'ensemblID', label: 'Ensembl ID', displayLabel: 'Ensembl ID' },
      { value: 'uniprotID', label: 'Uniprot ID', displayLabel: 'Uniprot ID' },
      { value: 'geneID', label: 'Gene ID', displayLabel: 'Gene ID' },
    ],
    columnHeaders: {
      symbol: 'Gene Symbol',
      name: 'Gene Name',
      ensemblID: 'Ensembl ID',
      uniprotID: 'Uniprot ID',
      geneID: 'Gene ID',
    },
    secondColumnHeaders: {
      symbol: 'Gene ID',
      name: 'Gene ID',
      ensemblID: 'Gene Symbol',
      uniprotID: 'Gene Symbol',
      geneID: 'Gene Symbol',
    },
    thirdColumnHeaders: {
      symbol: 'ENSEMBL ID',
      name: 'Gene Symbol',
      ensemblID: 'Gene Symbol',
      uniprotID: 'Gene Symbol',
      geneID: 'ENSEMBL ID',
    },
    resultFields: {
      symbol: 'symbol',
      name: 'term',
      ensemblID: 'ensembl_ids',
      uniprotID: 'proteinStrIDs',
      geneID: 'geneID',
    },
    secondColumnFields: {
      symbol: 'geneID',
      name: 'normalizedId',
      ensemblID: 'geneID',
      uniprotID: 'geneID',
      geneID: 'symbol',
    },
    thirdColumnFields: {
      symbol: 'ensembl_ids',
      name: 'normalizedName',
      ensemblID: 'symbol',
      uniprotID: 'symbol',
      geneID: 'ensembl_ids',
    },
    formatExamples: {
      name: 'Names are: tumor necrosis factor, interleukin 10, NRF2',
      symbol: 'Symbols are: BRCA1, TP53, EGFR',
      ensemblID: 'Ensembl IDs are: ENSG00000141510, ENSG00000134982, ENSG00000157764',
      uniprotID: 'Uniprot IDs are: P04637, Q9BYF1, P38398',
      geneID: 'IDs are: 672, 7157, 1956',
    },
  },
  CHEMICALS: {
    filters: [
      { value: 'name', label: 'Chemical Name', displayLabel: 'Chemical Name' },
      { value: 'CHEMBL', label: 'Chembl ID', displayLabel: 'CHEMBL ID' },
      { value: 'CHEBI', label: 'Chebi ID', displayLabel: 'CHEBI ID' },
      { value: 'DRUGBANK', label: 'Drugbank ID', displayLabel: 'DRUGBANK ID' },
      { value: 'MESH', label: 'MESH', displayLabel: 'MESH ID' },
      { value: 'PUBCHEM', label: 'Pubchem ID', displayLabel: 'PUBCHEM ID' },
    ],
    columnHeaders: {
      name: 'Chemical Name',
      CHEMBL: 'CHEMBL ID',
      CHEBI: 'CHEBI ID',
      DRUGBANK: 'DRUGBANK ID',
      MESH: 'MESH ID',
      PUBCHEM: 'PUBCHEM ID',
    },
    secondColumnHeaders: {
      name: 'Chembl ID',
      CHEMBL: 'Chembl Name',
      CHEBI: 'Chembl Name',
      DRUGBANK: 'Chembl Name',
      MESH: 'Chembl Name',
      PUBCHEM: 'Chembl Name',
    },
    thirdColumnHeaders: {
      name: 'Chembl Name',
      CHEMBL: 'Select',
      CHEBI: 'Chembl ID',
      DRUGBANK: 'Chembl ID',
      MESH: 'Chembl ID',
      PUBCHEM: 'Chembl ID',
    },
    resultFields: {
      name: 'term',
      CHEMBL: 'chembl',
      CHEBI: 'chebi',
      DRUGBANK: 'drugbank',
      MESH: 'mesh',
      PUBCHEM: 'pubchem',
    },
    secondColumnFields: {
      name: 'chemID',
      CHEMBL: 'chemPrefName',
      CHEBI: 'chemPrefName',
      DRUGBANK: 'chemPrefName',
      MESH: 'chemPrefName',
      PUBCHEM: 'chemPrefName',
    },
    formatExamples: {
      name: 'Names are: Minoxidil, Doxorubicin, Cholecalciferol',
      CHEBI: 'ChEBI IDs are: 16236, 18258, 18243',
      DRUGBANK: 'DrugBank IDs are: DB00002, DB00898, DB00997',
      MESH: 'MeSH IDs are: D002945, D000431, D004317',
      CHEMBL: 'ChEMBL IDs are: CHEMBL1251, CHEMBL4382, CHEMBL357328',
      PUBCHEM: 'PubChem IDs are: 702, 2244, 60961',
    },
  },
};

export const getSearchConfig = (searchType: SearchType): SearchTypeConfig => {
  return searchConfig[searchType];
};

export const getFilterOptions = (searchType: SearchType): FilterConfig[] => {
  return getSearchConfig(searchType).filters;
};

export const getColumnHeader = (
  searchType: SearchType,
  selectedFilter: string,
  columnIndex: 'primary' | 'secondary' | 'tertiary' = 'primary',
): string => {
  const config = getSearchConfig(searchType);

  if (columnIndex === 'secondary') {
    return config.secondColumnHeaders?.[selectedFilter] || 'N/A';
  }
  if (columnIndex === 'tertiary') {
    return config.thirdColumnHeaders?.[selectedFilter] || 'N/A';
  }

  return config.columnHeaders[selectedFilter] || selectedFilter;
};

export const getFormatExample = (searchType: SearchType, selectedFilter: string): string => {
  return getSearchConfig(searchType).formatExamples[selectedFilter] || '';
};
